import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, IdCard, Clock, CheckCircle, XCircle, Star, AlertTriangle, ArrowRight, Timer } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import API from "@/lib/api";
import { toast } from "sonner";
import { CUSTOMER } from "@/constants/testIds";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  under_review: "bg-[#eef1f8] text-[#0c1e41] border-[#c0cfe8]",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  suspended: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_ICONS = {
  pending: Clock,
  under_review: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  suspended: XCircle,
};

export default function SenderIDs() {
  const navigate = useNavigate();
  const [senderIds, setSenderIds] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [subActive, setSubActive] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ sender_id: "", purpose: "", business_name: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ids, sub] = await Promise.all([
        API.get("/sender-ids"),
        API.get("/subscriptions/current"),
      ]);
      setSenderIds(ids.data.sender_ids || []);
      setSubscription(sub.data.subscription);
      setSubActive(sub.data.active || false);
      setTrialDaysRemaining(sub.data.trial_days_remaining ?? null);
    } catch { toast.error("Failed to load sender IDs"); }
    finally { setLoading(false); }
  };

  const hasActiveSub = subActive;
  const isTrial = subscription?.status === "trial";
  const senderIdLimit = subscription?.sender_id_limit ?? 0;
  const usedSlots = senderIds.filter(s => ["pending", "under_review", "approved"].includes(s.status)).length;
  const atLimit = senderIdLimit !== -1 && usedSlots >= senderIdLimit;

  const handleRequestClick = () => {
    if (!hasActiveSub) {
      toast.info("Subscribe to get a 5-day trial and create Sender IDs");
      navigate("/dashboard/subscribe");
      return;
    }
    if (atLimit) {
      toast.error(`You've used all ${senderIdLimit} Sender ID slot(s). Upgrade your plan for more.`);
      navigate("/dashboard/subscribe");
      return;
    }
    setModal(true);
  };

  const save = async () => {
    if (!form.sender_id || !form.purpose || !form.business_name) { toast.error("Fill all fields"); return; }
    if (form.sender_id.length < 3 || form.sender_id.length > 11) { toast.error("Sender ID must be 3–11 characters"); return; }
    if (!/^[a-zA-Z0-9]+$/.test(form.sender_id)) { toast.error("Sender ID can only contain letters and numbers"); return; }
    setSaving(true);
    try {
      await API.post("/sender-ids/request", form);
      toast.success("Sender ID request submitted!");
      setModal(false);
      setForm({ sender_id: "", purpose: "", business_name: "" });
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "SUBSCRIPTION_REQUIRED") {
        toast.error("You need an active subscription to request a Sender ID");
        navigate("/dashboard/subscribe");
      } else if (detail === "TRIAL_EXPIRED") {
        toast.error("Your 5-day trial has expired. Complete payment to restore access.");
        navigate("/dashboard/subscribe");
      } else if (detail.startsWith("SENDER_ID_LIMIT_REACHED")) {
        toast.error("Sender ID limit reached for your plan. Upgrade to add more.");
        navigate("/dashboard/subscribe");
      } else {
        toast.error(detail || "Submission failed");
      }
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={CUSTOMER.senderIdsPanel} className="space-y-5">

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Sender IDs</h1>
          <p className="text-sm text-slate-500 mt-1">
            {senderIds.filter(s => s.status === "approved").length} approved
            {" · "}{senderIds.filter(s => ["pending","under_review"].includes(s.status)).length} pending
            {hasActiveSub && senderIdLimit !== -1 && <span className="ml-2">· {usedSlots}/{senderIdLimit} slots used</span>}
          </p>
        </div>
        <Button
          onClick={handleRequestClick}
          size="sm"
          className={`h-9 shadow-sm ${!hasActiveSub || atLimit ? "bg-amber-500 hover:bg-amber-600" : "bg-[#0c1e41] hover:bg-[#091629]"}`}
          data-testid={CUSTOMER.requestSenderIdButton}>
          <Plus size={14} className="mr-2" />
          {!hasActiveSub ? "Subscribe to Request" : atLimit ? "Upgrade Plan" : "Request Sender ID"}
        </Button>
      </div>

      {/* No subscription banner */}
      {!loading && !hasActiveSub && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="subscription-required-banner">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-800">Subscription Required</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Subscribe to any plan and get a <strong>5-day free trial</strong> — Sender IDs are unlocked immediately.
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard/subscribe")}
            className="shrink-0 flex items-center gap-1 text-xs font-bold text-amber-700 hover:text-amber-900 border border-amber-300 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors"
            data-testid="subscribe-now-btn">
            Subscribe <ArrowRight size={11} />
          </button>
        </div>
      )}

      {/* Trial countdown banner */}
      {!loading && hasActiveSub && isTrial && (
        <div
          className={`border rounded-xl p-4 flex items-center gap-3 ${(trialDaysRemaining ?? 5) <= 1 ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}
          data-testid="trial-countdown-banner">
          <Timer size={16} className={(trialDaysRemaining ?? 5) <= 1 ? "text-red-600" : "text-blue-600"} />
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-800">
              {trialDaysRemaining === 0 ? "Trial expires today!" : trialDaysRemaining === 1 ? "1 day left in trial" : `${trialDaysRemaining} days left in trial`}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Complete your bank transfer to activate <strong>{subscription?.plan_name}</strong> and keep your Sender IDs.
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard/subscribe")}
            className={`shrink-0 flex items-center gap-1 text-xs font-bold border px-3 py-1.5 rounded-lg transition-colors ${(trialDaysRemaining ?? 5) <= 1 ? "text-red-700 border-red-300 hover:bg-red-100" : "text-blue-700 border-blue-300 hover:bg-blue-100"}`}
            data-testid="trial-pay-now-btn">
            Pay Now <ArrowRight size={11} />
          </button>
        </div>
      )}

      {/* Active (non-trial) subscription slots */}
      {!loading && hasActiveSub && !isTrial && (
        <div className={`border rounded-xl p-4 flex items-center gap-3 ${atLimit ? "bg-red-50 border-red-200" : "bg-[#eef1f8] border-[#c0cfe8]"}`}>
          <Star size={16} className={atLimit ? "text-red-600" : "text-[#0c1e41]"} />
          <p className="text-sm text-slate-700">
            <strong>{subscription.plan_name}</strong> · {senderIdLimit === -1 ? "Unlimited" : `${usedSlots} of ${senderIdLimit}`} Sender ID{senderIdLimit !== 1 ? "s" : ""} used
            {atLimit && <span className="text-red-600 font-semibold ml-2">— limit reached</span>}
          </p>
          {atLimit && (
            <button onClick={() => navigate("/dashboard/subscribe")} className="ml-auto text-xs font-bold text-red-600 hover:underline flex items-center gap-1">
              Upgrade <ArrowRight size={11} />
            </button>
          )}
        </div>
      )}

      {/* Info card */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-700 mb-1">What is a Sender ID?</p>
        <p className="text-xs text-slate-500 leading-relaxed">
          A Sender ID is the name that appears on your SMS (e.g. "MYBRAND" instead of a number). Requests are reviewed within 24–48 hours. Must be 3–11 alphanumeric characters.
        </p>
      </div>

      {/* Sender ID list */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : senderIds.length === 0 ? (
        <div className="text-center py-14">
          <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <IdCard size={24} className="text-slate-400" />
          </div>
          <p className="text-sm text-slate-500 mb-4">No sender IDs yet. Request one to start sending branded SMS.</p>
          <Button onClick={handleRequestClick} className={hasActiveSub ? "bg-[#0c1e41] hover:bg-[#091629]" : "bg-amber-500 hover:bg-amber-600"}>
            {hasActiveSub ? "Request Sender ID" : "Subscribe to Request"}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {senderIds.map((s) => {
            const Icon = STATUS_ICONS[s.status] || Clock;
            return (
              <div key={s._id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      s.status === "approved" ? "bg-emerald-50 border border-emerald-200"
                      : s.status === "suspended" ? "bg-slate-100 border border-slate-200"
                      : "bg-amber-50 border border-amber-200"
                    }`}>
                      <Icon size={18} className={
                        s.status === "approved" ? "text-emerald-600"
                        : s.status === "suspended" ? "text-slate-400"
                        : "text-amber-600"
                      } />
                    </div>
                    <div>
                      <span className="font-mono text-lg font-extrabold text-slate-900">{s.sender_id}</span>
                      <div className="text-xs text-slate-500 mt-0.5">{s.business_name}</div>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-xs capitalize ${STATUS_STYLES[s.status] || ""}`}>
                    {s.status?.replace("_", " ")}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <span className="font-medium text-slate-700">Purpose:</span> {s.purpose}
                </div>
                {s.status === "suspended" && (
                  <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700">
                    {s.suspension_reason || "Suspended — complete payment to restore"}
                  </div>
                )}
                {s.admin_note && s.status !== "suspended" && (
                  <div className={`mt-3 text-xs px-3 py-2 rounded-lg ${s.status === "rejected" ? "bg-red-50 border border-red-200 text-red-700" : "bg-[#eef1f8] border border-[#c0cfe8] text-[#0c1e41]"}`}>
                    Admin note: {s.admin_note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Request Modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-slate-900">Request Sender ID</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Sender ID * (3–11 chars, alphanumeric)</Label>
              <Input
                value={form.sender_id}
                onChange={(e) => setForm({ ...form, sender_id: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
                maxLength={11}
                placeholder="MYBRAND"
                className="bg-white border-slate-300 text-slate-900 h-10 font-mono uppercase tracking-widest"
                data-testid="sender-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Business Name *</Label>
              <Input
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="Acme Nigeria Ltd"
                className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400"
                data-testid="business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Purpose / Use Case *</Label>
              <Textarea
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                placeholder="e.g. OTP and transaction alerts for e-commerce customers"
                rows={3}
                className="bg-white border-slate-300 text-slate-900 text-sm resize-none"
                data-testid="purpose-input"
              />
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-[#0c1e41] hover:bg-[#091629]" data-testid="submit-sender-id-btn">
              {saving ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
